import { useMemo } from "react";

import { SYSTEM_COLORS } from "../../../../components/system.colors";
import { compactCurrencyFormatter } from "../../../../utils/formatter/formatter";

import { SectionTitle } from "./SectionTitle";

const dec = (value, digits = 1) => (value === null || value === undefined ? "—" : value.toFixed(digits));

const GAUGE_GRADIENT = `linear-gradient(to right,
  ${SYSTEM_COLORS.RED.CORAL_RED} 0%,
  ${SYSTEM_COLORS.ORANGE.TANGERINE} 10%,
  ${SYSTEM_COLORS.YELLOW.AMBER} 22%,
  ${SYSTEM_COLORS.GREEN.FOREST_GREEN} 42%,
  ${SYSTEM_COLORS.YELLOW.AMBER} 62%,
  ${SYSTEM_COLORS.ORANGE.TANGERINE} 80%,
  ${SYSTEM_COLORS.RED.CORAL_RED} 100%
)`;

const CoverageGauge = ({ cobertura, idealRange, domainMax }) => {
  const hasCobertura = cobertura !== null && cobertura !== undefined;
  const markerPosition = Math.min((cobertura / domainMax) * 100, 100);
  const idealCenter = ((idealRange[0] + idealRange[1]) / 2 / domainMax) * 100;
  const ticks = [0, 50, 100, 150, 200];

  return (
    <div>
      <div style={{ position: "relative", marginBlockEnd: "0.4rem", blockSize: "1.1rem" }}>
        {hasCobertura && (
          <span style={{
            position: "absolute", left: `${markerPosition}%`, transform: "translateX(-50%)",
            fontSize: "0.78rem", fontWeight: 700, color: SYSTEM_COLORS.BLACK.EERIE_BLACK, whiteSpace: "nowrap",
          }}>
            {`${Math.round(cobertura)} dias`}
          </span>
        )}
      </div>

      <div style={{ position: "relative", blockSize: "1.4rem", borderRadius: "999px", background: GAUGE_GRADIENT, overflow: "visible" }}>
        {hasCobertura && (
          <div style={{
            position: "absolute", left: `${markerPosition}%`, top: "-0.3rem", blockSize: "2rem",
            borderLeft: `2px dashed ${SYSTEM_COLORS.BLACK.EERIE_BLACK}`,
          }} />
        )}
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", marginBlockStart: "0.5rem", fontSize: "0.75rem", color: SYSTEM_COLORS.GRAY.GREY }}>
        {ticks.map((tick, index) => (
          <span key={tick}>{index === ticks.length - 1 ? `${tick} dias` : tick}</span>
        ))}
      </div>

      <div style={{ position: "relative", blockSize: "1.2rem" }}>
        <span style={{
          position: "absolute", left: `${idealCenter}%`, transform: "translateX(-50%)",
          fontSize: "0.72rem", color: SYSTEM_COLORS.GRAY.MEDIUM_GRAY, whiteSpace: "nowrap",
        }}>
          {`▲ ideal (${idealRange[0]}-${idealRange[1]} dias)`}
        </span>
      </div>
    </div>
  );
};

const CoverageRankList = ({ title, description, items, color, background }) => {
  const maxDias = useMemo(() => (items.length ? Math.max(...items.map((item) => item.dias)) : 0), [items]);

  return (
    <div>
      <div style={{ fontSize: "0.75rem", fontWeight: 700, color: SYSTEM_COLORS.GRAY.CHARCOAL, letterSpacing: "0.4px", textTransform: "uppercase" }}>
        {title}
      </div>
      <p style={{ fontSize: "0.78rem", color: SYSTEM_COLORS.GRAY.GREY, margin: "0.3rem 0 1.1rem" }}>
        {description}
      </p>

      {items.length === 0 && (
        <p style={{ fontSize: "0.82rem", color: SYSTEM_COLORS.GRAY.GREY, fontStyle: "italic" }}>
          Nenhum produto nesta categoria.
        </p>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        {items.map((item, index) => (
          <div key={item.id} style={{ display: "flex", gap: "0.8rem", alignItems: "flex-start" }}>
            <span style={{ fontSize: "0.85rem", fontWeight: 700, color: SYSTEM_COLORS.GRAY.GREY, inlineSize: "1.1rem", flexShrink: 0 }}>
              {index + 1}
            </span>

            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.8rem" }}>
                <span style={{ fontSize: "0.9rem", fontWeight: 700, color: SYSTEM_COLORS.BLACK.EERIE_BLACK }}>
                  {item.name}
                </span>
                <span style={{
                  display: "inline-block", padding: "0.15rem 0.6rem", borderRadius: "999px",
                  background, color, fontSize: "0.78rem", fontWeight: 700, whiteSpace: "nowrap",
                }}>
                  {`${item.atCap ? "≥ " : ""}${item.dias} dias`}
                </span>
              </div>

              <div style={{ blockSize: "6px", borderRadius: "999px", background: SYSTEM_COLORS.GRAY.CULTURED, marginBlock: "0.4rem", overflow: "hidden" }}>
                <div style={{ inlineSize: `${(item.dias / maxDias) * 100}%`, blockSize: "100%", background: color, borderRadius: "999px" }} />
              </div>

              <span style={{ fontSize: "0.75rem", color: SYSTEM_COLORS.GRAY.GREY }}>
                {`${item.categoria} · capital ${compactCurrencyFormatter(item.capital)}`}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export function CoverageSection({ ctx }) {
  const { categoryName, cobertura, giro, idealRange, domainMax, example, lowestCoverage = [], highestCoverage = [] } = ctx || {};

  return (
    <section style={{ marginBlockStart: "2rem" }}>
      <SectionTitle
        title="COBERTURA"
        description="Cobertura é para quantos dias de venda o estoque atual dura. A cobertura da categoria é a média dos produtos ponderada pelo estoque em R$."
      />

      <div style={{
        background: SYSTEM_COLORS.WHITE.WHITE,
        border: `1px solid ${SYSTEM_COLORS.GRAY.LIGHT_GRAY}`,
        borderRadius: "14px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        padding: "1.5rem 2rem",
      }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1.6fr", gap: "2.5rem", alignItems: "center", paddingBlockEnd: "1.5rem" }}>
          <div>
            <div style={{ fontSize: "0.72rem", fontWeight: 700, color: SYSTEM_COLORS.GRAY.GREY, letterSpacing: "0.4px", textTransform: "uppercase" }}>
              {`Cobertura Atual · ${categoryName}`}
            </div>

            <div style={{ display: "flex", alignItems: "baseline", gap: "0.4rem", marginBlockStart: "0.3rem" }}>
              <span style={{ fontSize: "3rem", fontWeight: 800, color: SYSTEM_COLORS.BLACK.EERIE_BLACK, lineHeight: 1 }}>
                {cobertura === null || cobertura === undefined ? "—" : Math.round(cobertura)}
              </span>
              <span style={{ fontSize: "1.1rem", color: SYSTEM_COLORS.GRAY.GREY }}>dias</span>
            </div>

            <p style={{ fontSize: "0.85rem", color: SYSTEM_COLORS.GRAY.MEDIUM_GRAY, margin: "0.6rem 0 0" }}>
              {"Giro de "}
              <strong style={{ color: SYSTEM_COLORS.BLACK.EERIE_BLACK }}>{`${dec(giro)}x`}</strong>
              {" · faixa ideal entre "}
              <strong style={{ color: SYSTEM_COLORS.BLACK.EERIE_BLACK }}>{`${idealRange[0]} e ${idealRange[1]} dias`}</strong>
              {"."}
            </p>

            {/* <div style={{
              background: SYSTEM_COLORS.WHITE.WHITE_SMOKE,
              border: `1px solid ${SYSTEM_COLORS.GRAY.PLATINUM}`,
              borderRadius: "10px",
              padding: "0.8rem 1rem",
              marginBlockStart: "1rem",
              fontSize: "0.8rem",
              color: SYSTEM_COLORS.GRAY.MEDIUM_GRAY,
              lineHeight: 1.5,
            }}>
              {example}
            </div> */}
          </div>

          <CoverageGauge cobertura={cobertura} idealRange={idealRange} domainMax={domainMax} />
        </div>

        <div style={{ borderTop: `1px solid ${SYSTEM_COLORS.GRAY.PLATINUM}`, paddingBlockStart: "1.5rem", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2.5rem" }}>
          <CoverageRankList
            title="Menor Cobertura"
            description="Produtos com maior risco de ruptura — estoque acaba antes do previsto."
            items={lowestCoverage}
            color={SYSTEM_COLORS.GREEN.FOREST_GREEN}
            background={SYSTEM_COLORS.GREEN.FOREST_GREEN_10}
          />

          <CoverageRankList
            title="Maior Cobertura"
            description="Produtos com estoque parado — capital preso e risco de remarcação."
            items={highestCoverage}
            color={SYSTEM_COLORS.RED.BRICK_RED}
            background={SYSTEM_COLORS.RED.BRICK_RED_10}
          />
        </div>
      </div>
    </section>
  );
}
